import ObjectID from 'bson-objectid';
import { useCallback, useEffect, useState } from 'react';

import Composite from 'components/inputs/composite';

export interface AgenticRouterLabelValue {
  name?: string;
  minConfidence?: number | string;
}

interface CompositeFieldValue<T = string | number> {
  name: string;
  value: T;
}

interface CompositeLabelValue {
  repeatId: string;
  name?: CompositeFieldValue<string>;
  minConfidence?: CompositeFieldValue<number | string>;
}

interface CompositeLabelsValue {
  name: string;
  compositeValues: CompositeLabelValue[];
}

interface AgenticRouterLabelsInputProps {
  configuration: any[];
  defaultValue?: AgenticRouterLabelValue[];
  value?: AgenticRouterLabelValue[];
  displayMode?: string;
  onChange?: (value: AgenticRouterLabelValue[]) => void;
}

const getStringValue = (field?: CompositeFieldValue<string>) => field?.value ?? '';
const ROUTER_LABEL_FIELDS = new Set(['name', 'minConfidence']);
const ROUTER_LABEL_FIELD_WIDTHS: Record<string, string> = {
  name: '240px',
  minConfidence: '180px',
};

const transformToComposite = (labels?: AgenticRouterLabelValue[]): CompositeLabelsValue | undefined => {
  if (!labels?.length) {
    return undefined;
  }

  return {
    name: 'agenticRouterLabelsComposite',
    compositeValues: labels.map((label) => ({
      repeatId: ObjectID.generate(),
      name: { name: 'name', value: label.name ?? '' },
      minConfidence: { name: 'minConfidence', value: label.minConfidence ?? 0 },
    })),
  };
};

const transformToLabels = (compositeValue?: CompositeLabelsValue): AgenticRouterLabelValue[] => {
  if (!compositeValue?.compositeValues?.length) {
    return [];
  }

  return compositeValue.compositeValues
    .map((label) => ({
      name: getStringValue(label.name),
      minConfidence: label.minConfidence?.value ?? 0,
    }))
    .filter((label) => label.name);
};

export const AgenticRouterLabelsInput = ({
  configuration,
  defaultValue,
  value,
  displayMode,
  onChange,
}: AgenticRouterLabelsInputProps) => {
  const [compositeValue, setCompositeValue] = useState<CompositeLabelsValue | undefined>();
  const filteredConfiguration = configuration
    .filter((field) => ROUTER_LABEL_FIELDS.has(field?.name))
    .map((field) => ({
      ...field,
      width: ROUTER_LABEL_FIELD_WIDTHS[field.name] || field.width,
    }));

  useEffect(() => {
    setCompositeValue(transformToComposite(defaultValue || value));
  }, [defaultValue, value]);

  const onCompositeChange = useCallback(
    (nextCompositeValue: CompositeLabelsValue) => {
      setCompositeValue(nextCompositeValue);
      onChange?.(transformToLabels(nextCompositeValue));
    },
    [onChange]
  );

  return (
    <Composite
      displayMode={displayMode}
      addText="Add Branch"
      name="agenticRouterLabelsComposite"
      onChange={onCompositeChange}
      configuration={filteredConfiguration}
      defaultValue={compositeValue}
      value={compositeValue}
      repeatable
    />
  );
};

export default AgenticRouterLabelsInput;
